import React from "react";

const AcceptedArticleTypes = () => {
  return (
    <React.Fragment>
      <h1>
        <strong>Accepted Article Types</strong>
      </h1>
      <p>
        Cureus accepts and publishes six different article types: Original Article, Review Article,
        Business Report, Case Study, Editorials, and Letters. To be considered for publication, each
        article type must include the sections listed below. Only original articles may include
        abstract subheadings. CJBE does not accept or publish article commentaries or letters to the
        editor. Please note that all articles must undergo peer review before publication.
      </p>

      <h2>
        <strong>Free Publication Eligibility</strong>
      </h2>
      <p>
        Original articles, Case Studies, Business Reports, Editorials, and Letters that exceed the
        specified number of authors or references will no longer be eligible for free publication.
        All review articles require the purchase of{" "}
        <a href="/cureus_editing_services" target="_blank" rel="noopener">
          Preferred Editing.
        </a>{" "}
        regardless of the number of authors or references. Please note that staying within the
        limits does not guarantee free publication, as articles with too many errors will still
        require{" "}
        <a href="/cureus_editing_services" target="_blank" rel="noopener">
          Preferred Editing.
        </a>
      </p>

      <h2>
        <strong>Original Article</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Original articles are original research studies classified as primary literature. An
            original article is the most common type of journal manuscript used to publish full
            reports of data from research. Original articles may also be referred to as research
            articles, research, or just articles, as this format is suitable for many different
            fields and different types of studies. Authors can add limitations and scope for future
            study in the Results and Discussion section under a separate subheading.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction</div>
            <div>Research Method</div>
            <div>Results and Discussion</div>
            <div>Conclusion</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free Original Articles Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font .space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font .space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Review Article</strong>
      </h2>

      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Review articles give an overview of existing literature in a field, often identifying
            specific problems or issues and providing a balanced analysis derived from available
            published work on the topic. Review articles provide a comprehensive summary of research
            on the selected topic, and a perspective on the state of the field and where it is
            heading.
          </p>
          <p>
            Review articles can be one of two types, broadly speaking: literature reviews or
            systematic reviews. Bibliometric analysis and meta-analysis articles should also be
            submitted as review articles. Authors can add limitations and scope for future study in
            the discussion section under a separate subheading.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction and Background</div>
            <div>Review</div>
            <div>Conclusions</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Preferred Editing Required for All Review Articles</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Case Study</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            The primary objective of a case study is to alert other professionals in the field to
            the possibility of encountering similar challenges. A study involving a series of five
            or fewer cases should be submitted in the format of a case study. If the studies mention
            any images from tools such as blueprints, schematics, simulations, or prototypes, they
            should be included in the document. Submissions that combine a case discussion with a
            literature review should be categorized as Business/Economics case studies. Detailed
            author contributions are essential and should be acknowledged, especially in a case
            study with more than five authors.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction</div>
            <div>Case Description</div>
            <div>Discussion</div>
            <div>Conclusions</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free Case Studies Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Letters</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Letters are articles related to science, policy, research trends, business trends,
            exciting discoveries, etc. A "letter" is a concise, focused article that typically
            addresses a specific issue, presents a brief but significant finding, or contributes a
            novel perspective on a relevant topic. Letters can include a Title, Abstract,
            Introduction, Research Method, Result and Discussion, Conclusion.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction</div>
            <div>Research Method</div>
            <div>Results and Discussion</div>
            <div>Conclusion</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free Letters Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Business Report</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Business reports are formal documents designed to convey a company's information
            regarding performance, activities, or other important data in a clear and easily
            accessible format. These data-driven reports help in communication with stakeholders and
            support business growth. They may cover ongoing performance, financial results, market
            analysis, strategic plans, or other relevant topics. Typically, business reports are
            divided into sections that allow different readers to access distinct levels of
            information. A business report describes the process, progress, or results of an
            organization, which may include the state of a business or a scientific research
            problem. It might also include recommendations and conclusions based on the findings.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Introduction</div>
            <div>Detailed Report</div>
            <div>Discussion</div>
            <div>Conclusions</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free Business Reports Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>

      <h2 className="space-top-xxl">
        <strong>Editorial</strong>
      </h2>
      <div className="article-type-info row">
        <div className="medium-offset-0 medium-8 columns">
          <p>
            Editorials are opinion or perspective pieces on a relevant, timely topic. Editorials
            will only be approved if submitted by senior academicians or researchers with an
            established publishing history.
          </p>
        </div>
        <div className="small-offset-5 small-6 medium-offset-1 medium-3 columns sections">
          <div className="small-font blue">
            <strong>SECTIONS</strong>
          </div>
          <div className="article-section-list">
            <div>Abstract</div>
            <div>Editorial</div>
            <div>References</div>
          </div>
        </div>
        <div className="small-12 columns centered">
          <div className="small-font blue space-bottom-s">
            <strong>Free Editorials Limited To</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-2 medium-4 columns centered">
          <div className="large-font space-bottom-xxs">
            <strong>30</strong>
          </div>
          <div className="small-font">
            <strong>Authors</strong>
          </div>
        </div>
        <div className="small-12 medium-offset-0 medium-4 columns centered end">
          <div className="large-font space-bottom-xxs">
            <strong>200</strong>
          </div>
          <div className="small-font">
            <strong>References</strong>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AcceptedArticleTypes;
